/**
 * Revolt Autopaint GSAP Animations Engine
 * Integrates smooth scroll reveals, metrics count-ups, and page load entrance transitions.
 * Uses high-performance native IntersectionObserver to trigger GSAP animations, 
 * completely eliminating height calculation or layout shift issues with Swiper.
 * Fully compatible with Shopify Theme Editor reload events.
 */
const runRevoltAnimations = () => {
  if (typeof gsap === 'undefined') {
    console.warn('GSAP is not loaded.');
    return;
  }
  
  const initRevoltAnimations = (container = document) => {
    // 1. Homepage Hero: Entrance animations removed for instantaneous First Contentful Paint (FCP)

    // 2. Section Headers slide up & fade
    gsap.utils.toArray(container.querySelectorAll('.revolt-section-header, .revolt-steps__header, .revolt-testimonials__header, .revolt-wall-of-fame__header')).forEach(header => {
      const titleGroup = header.querySelector('.revolt-section-header__title-group, .revolt-steps__title-group, .revolt-testimonials__title-group, .revolt-wall-of-fame__title-group');
      const viewAll = header.querySelector('.revolt-section-header__view-all, .revolt-wall-of-fame__view-all, .revolt-testimonials__nav');
      
      if (titleGroup || viewAll) {
        if (titleGroup) gsap.set(titleGroup, { opacity: 0, y: 30 });
        if (viewAll) gsap.set(viewAll, { opacity: 0, x: 20 });
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (titleGroup) gsap.to(titleGroup, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" });
              if (viewAll) gsap.to(viewAll, { opacity: 1, x: 0, duration: 0.8, ease: "power2.out", delay: 0.1 });
              observer.unobserve(header);
            }
          });
        }, { rootMargin: "0px 0px -50px 0px" });
        
        observer.observe(header);
      }
    });

    // 3. Grid / Card staggered animations
    const cardSelectors = [
      '.revolt-card', 
      '.revolt-bento-item', 
      '.revolt-top-card',
      '.revolt-testimonial-card',
      '.revolt-step-item',
      '.revolt-advantage__list-item',
      '.revolt-wall-of-fame__item',
      '.revolt-about-manifesto__pillar',
      '.revolt-about-tech__feature',
      '.revolt-about-timeline__milestone',
      '.revolt-about-values__value'
    ].join(', ');

    // Group cards by their parent sections for highly robust triggering.
    // Using IntersectionObserver completely bypasses ScrollTrigger's offset issues with Swiper.
    gsap.utils.toArray(container.querySelectorAll('.revolt-section')).forEach(section => {
      const cards = section.querySelectorAll(cardSelectors);
      if (cards.length) {
        gsap.set(cards, { opacity: 0, y: 40 });
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              gsap.to(cards, {
                opacity: 1,
                y: 0,
                stagger: 0.1,
                duration: 0.8,
                ease: "power2.out"
              });
              observer.unobserve(section);
            }
          });
        }, { rootMargin: "0px 0px -80px 0px" });
        
        observer.observe(section);
      }
    });

    // Fallback for isolated cards that are not in a recognized section
    gsap.utils.toArray(container.querySelectorAll(cardSelectors)).forEach(card => {
      // If card was already animated by its parent section trigger, skip it
      if (card.style.opacity !== '') return;
      
      gsap.set(card, { opacity: 0, y: 30 });
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            gsap.to(card, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power2.out"
            });
            observer.unobserve(card);
          }
        });
      }, { rootMargin: "0px 0px -50px 0px" });
      
      observer.observe(card);
    });

    // 4. Columns Side Slide-in (Image + Text or Video + Text grids)
    const leftCols = container.querySelectorAll('.revolt-video-content__video-col, .revolt-about-story__image-col, .revolt-advantage__image-col');
    const rightCols = container.querySelectorAll('.revolt-video-content__content-col, .revolt-about-story__content-col, .revolt-advantage__text-col');
    
    leftCols.forEach(col => {
      gsap.set(col, { opacity: 0, x: -50 });
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            gsap.to(col, { opacity: 1, x: 0, duration: 1, ease: "power3.out" });
            observer.unobserve(col);
          }
        });
      }, { rootMargin: "0px 0px -80px 0px" });
      
      observer.observe(col);
    });

    rightCols.forEach(col => {
      gsap.set(col, { opacity: 0, x: 50 });
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            gsap.to(col, { opacity: 1, x: 0, duration: 1, ease: "power3.out" });
            observer.unobserve(col);
          }
        });
      }, { rootMargin: "0px 0px -80px 0px" });
      
      observer.observe(col);
    });

    // 5. About Page Stats Count-Up Animation
    gsap.utils.toArray(container.querySelectorAll('.revolt-stat-card')).forEach(card => {
      const metricEl = card.querySelector('.revolt-stat-card__metric');
      if (!metricEl) return;
      const originalText = metricEl.innerText;
      
      const numberMatch = originalText.match(/([\d.]+)/);
      if (!numberMatch) return;
      
      const targetNumber = parseFloat(numberMatch[0]);
      const prefix = originalText.split(numberMatch[0])[0] || "";
      const suffix = originalText.split(numberMatch[0])[1] || "";
      const isFloat = numberMatch[0].includes('.');

      const obj = { value: 0 };
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            gsap.fromTo(obj, 
              { value: 0 }, 
              {
                value: targetNumber,
                duration: 1.8,
                ease: "power2.out",
                onUpdate: () => {
                  const formattedVal = isFloat ? obj.value.toFixed(1) : Math.round(obj.value);
                  metricEl.innerText = `${prefix}${formattedVal}${suffix}`;
                }
              }
            );
            observer.unobserve(card);
          }
        });
      }, { rootMargin: "0px 0px -50px 0px" });
      
      observer.observe(card);
    });

    // 6. Product Detail Page Entrance Stagger
    const productInfo = container.querySelector('.product__info-container');
    const productMedia = container.querySelector('.product__media-wrapper');
    
    if (productInfo) {
      const infoChildren = Array.from(productInfo.children);
      gsap.from(infoChildren, {
        opacity: 0,
        y: 25,
        stagger: 0.1,
        duration: 0.8,
        ease: "power3.out"
      });
    }
    if (productMedia) {
      gsap.from(productMedia, {
        opacity: 0,
        x: -40,
        duration: 1,
        ease: "power3.out"
      });
    }

    // 7. Collection Page Header & Product Grid Entrance
    const collectionBanner = container.querySelector('.main-collection-banner');
    const collectionGrid = container.querySelector('.main-collection-product-grid');
    
    if (collectionBanner) {
      gsap.from(collectionBanner, {
        opacity: 0,
        y: -35,
        duration: 0.8,
        ease: "power3.out"
      });
    }
    if (collectionGrid) {
      const gridItems = collectionGrid.querySelectorAll('.grid__item, .revolt-card');
      if (gridItems.length) {
        gsap.set(gridItems, { opacity: 0, y: 40 });
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              gsap.to(gridItems, {
                opacity: 1,
                y: 0,
                stagger: 0.08,
                duration: 0.8,
                ease: "power3.out"
              });
              observer.unobserve(collectionGrid);
            }
          });
        }, { rootMargin: "0px 0px -80px 0px" });
        
        observer.observe(collectionGrid);
      }
    }
  };

  // Run initial page load animations
  initRevoltAnimations();

  // Listen for section reloads in Shopify Theme Customizer
  document.addEventListener('shopify:section:load', (event) => {
    const sectionId = event.detail.sectionId;
    const loadedSection = document.getElementById(`shopify-section-${sectionId}`);
    if (loadedSection) {
      initRevoltAnimations(loadedSection);
    }
  });
};

// Bulletproof DOM ready check to prevent race conditions with async/deferred scripts
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runRevoltAnimations);
} else {
  runRevoltAnimations();
}
